<template>
  <div>
    <div class="form-group">
      <label for="email">{{ $t('emailPlaceholder') }}</label>
      <input
        type="email"
        name="email"
        id="email"
        :placeholder="$t('emailPlaceholder')"
        v-model.trim="email"
        v-validate="'required|email'"
        :class="{'input': true, 'danger-border': errors.has('email') || !!emailErrorMessage}"
        @blur="showError.email = true"
      />
      <p class="error-info">
        <span v-show="errors.has('email') && showError.email">{{ errors.first('email') }}</span>
        <span v-show="showError.email">{{ emailErrorMessage }}</span>
      </p>
    </div>

    <div class="form-group">
      <label for="password">{{ $t('passwordPlaceholder') }}</label>
      <input
        type="password"
        name="password"
        id="password"
        v-if="!isShowPwd"
        :placeholder="$t('passwordPlaceholder')"
        v-model.trim="password"
        v-validate="'required|min:6|max:20'"
        @keyup.enter="doLogin"
        @blur="showError.password = true"
        :class="{'input pwd': true, 'danger-border': errors.has('password') || !!pwdErrorMessage}"
      />
      <input
        type="text"
        name="password"
        id="password"
        v-if="isShowPwd"
        :placeholder="$t('passwordPlaceholder')"
        v-model.trim="password"
        v-validate="'required|min:6|max:20'"
        @keyup.enter="doLogin"
        @blur="showError.password = true"
        :class="{'input pwd': true, 'danger-border': errors.has('password') || !!pwdErrorMessage}"
      />
      <span class="show-pwd" @click="showPwd">
        <img src="/static/img/dashboard/ic_eye.svg" v-show="isShowPwd" alt="" />
        <img src="/static/img/dashboard/ic_eye_off.svg" v-show="!isShowPwd" alt="" />
      </span>
      <p class="error-info">
        <span v-show="errors.has('password') && showError.password">{{ errors.first('password') }}</span>
        <span v-show="showError.password">{{ pwdErrorMessage }}</span>
      </p>
    </div>

    <button type="button" class="form-submit line-button" @click="doLogin" v-if="!isLoading">{{ $t('loginText') }}</button>
    <spinner class="spinner-component" :show="isLoading"></spinner>
    <p class="forgot-password">
      <router-link to="/forgotpw">{{$t('forgotPassword')}}</router-link>
      <router-link to="/register" class="register">{{ $t('signUp') }}</router-link>
    </p>
  </div>
</template>

<style rel="stylesheet/scss" lang="scss" scoped>
@import '../../../assets/scss/formstyle';

div.form-group {
  input {
    margin-top: .08rem;
    height: .54rem;
    line-height: .54rem;
    border: 1px solid #E0D7E3;
    border-radius: 42px;
    padding: 0 24px;
    font-size: 16px;
    &::placeholder {
      color: #ccc;
    }
    &.pwd {
      padding-right: 50px;
    }
  }
  .show-pwd {
    right: 24px;
    bottom: 37px;
  }
}

.forgot-password {
  margin-top: 0.4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  a {
    letter-spacing: 0;
    font-size: 16px;
    line-height: 0.16rem;
    color: #999999;
    &:first-child:hover {
      text-decoration: underline;
    }
    &.register {
      display: block;
      width: 120px;
      height: 44px;
      line-height: 44px;
      text-align: center;
      border: 1px solid #E0D7E3;
      border-radius: 42px;
      background: #FFF;
    }
  }
}

.form-submit {
  margin: .16rem 0 0;
  height: 54px;
  line-height: 54px;
  font-size: 18px;
  font-weight: 600;
  background-image: linear-gradient(125deg, #416CF8 0%, #EC55FF 100%);
  border-radius: 42px;
  position: relative;
  &::before {
    content: "";
    position: absolute;
    top: 4px;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(125deg, #416cf8 0%, #ec55ff 100%);
    border-radius: 42px;
    filter: blur(4px);
    -webkit-filter: blur(4px);
    -moz-filter: blur(4px);
    -ms-filter: blur(4px);
    -o-filter: blur(4px);
    z-index: -1;
  }
}

@media (max-width: 767px) {
  div.form-group {
    input {
      height: 0.49rem;
      line-height: .49rem;
      font-size: 14px;
      padding: 0 16px;
    }
    .show-pwd {
      right: 16px;
      bottom: 25px;
    }
  }
  .error-info {
    margin-top: 0.02rem;
  }
  .form-submit {
    margin-top: .24rem;
    height: 49px;
    line-height: 49px;
    font-size: 16px;
  }
  .forgot-password {
    margin-top: 0.24rem;
    flex-direction: column-reverse;
    a {
      width: 100%;
      &:first-child {
        font-size: 14px;
        line-height: 14px;
      }
      &.register {
        width: 100%;
        height: 49px;
        line-height: 49px;
        font-weight: 600;
        margin-bottom: 0.24rem;
      }
    }
  }
}
</style>

<script>
import Spinner from '../../Spinner.vue';
import locales from './lang';
import { mapState } from 'vuex';
import { SET_USER_INFO } from '../../../store/mutation-types';

export default {
  name: 'email-login-view',
  components: { Spinner },
  data: function () {
    return {
      email: '',
      password: '',
      emailErrorMessage: '',
      pwdErrorMessage: '',
      isLoading: false,
      isShowPwd: false,
      showError: {
        email: false,
        password: false
      }
    };
  },
  locales,
  computed: {
    ...mapState({
      redirectUrl: state => state.redirectUrl
    })
  },
  watch: {
    email() {
      this.emailErrorMessage = '';
      this.showError.email = false;
    },
    password() {
      this.pwdErrorMessage = '';
      this.showError.password = false;
    }
  },
  methods: {
    doLogin: function () {
      this.showError.email = true;
      this.showError.password = true;
      // Event snippet for sign_in_email page
      window.dataLayer && window.dataLayer.push({ event: 'sign_in_email' });
      this.$validator.validateAll().then(success => {
        if (!success) {
          return;
        }
        this.emailErrorMessage = '';
        this.pwdErrorMessage = '';
        this.isLoading = true;
        this.$http
          .post('api/login', {
            email: this.email,
            password: this.password,
            qr_token: window.sessionStorage.getItem('qr_token') || ''
          })
          .then(
            response => {
              switch (response.data.code) {
                case 0: {
                  if (response.data.meta) {
                    this.$toast(response.data.meta.qr_message);
                  } else {
                    window.localStorage.setItem('token', response.data.data.access_token);
                    window.sessionStorage.setItem('needUseNoCache', true);
                    this.$store.commit(SET_USER_INFO, response.data.data);
                    this.$router.push(this.redirectUrl);
                  }
                  break;
                }
                case 103: {
                  this.pwdErrorMessage = response.data.message;
                  this.input_error = true;
                  break;
                }
                default: {
                  this.emailErrorMessage = response.data.message;
                  this.input_error = true;
                }
              }
              this.isLoading = false;
            },
            response => {
              this.isLoading = false;
              this.emailErrorMessage = this.$t('failedRequest');
            }
          );
      });
    },
    showPwd: function () {
      this.isShowPwd = !this.isShowPwd;
    }
  }
};
</script>



// WEBPACK FOOTER //
// EmailLogin.vue?479ee468